// Librairies
import Axios from 'axios';
// Utils
import { getOfflineServices } from './OfflineServices';
import { isOfflineEnabled } from './OfflineFlag';
import { getDisplayFeature } from './ProjectSnapshot';
import { applyLinkChanges } from './handlers/LinkHandlers';
import { isCreationType } from './OutboxCompaction';

// Aides des services de lecture, sans effet hors pilote : copie des réponses de l'API, relecture de la copie sans réseau

// Erreur sans réponse de l'API (réseau, délai), hors annulation volontaire
export const isNetworkError = (error) => !!error && !error.response && !Axios.isCancel(error);

// Copie locale de l'utilisateur courant, seulement pour le pilote
const getSnapshot = () => (isOfflineEnabled() ? getOfflineServices()?.snapshot : null);

// Enregistre une liste du projet (occurrences, évènements, liaisons…) ; une erreur ne bloque pas l'écran
export const saveListToSnapshot = (projectId, name, data) => {
    getSnapshot()?.saveList(projectId, name, data).catch(console.error);
};

// Lit une liste du projet dans la copie
export const readListFromSnapshot = async (projectId, name) => getSnapshot()?.getList(projectId, name);

// Liste lue sur le serveur, complétée par les saisies encore en file (créées, modifiées ou supprimées sur l'appareil)
export const withPendingListItems = async (projectId, name, serverData) => {
    const services = isOfflineEnabled() ? getOfflineServices() : null;
    if (!services || serverData === undefined || serverData === null) return serverData;
    const ops = ((await services.outbox.list().catch(() => null)) || []).filter(op => Number(op.projectId) === Number(projectId));
    if (!ops.length) return serverData;
    if (name === 'linkedElements')
        return ops.filter(op => op.type.startsWith('link.'))
            .reduce((list, op) => applyLinkChanges(list, op.payload.toAdd || [], op.payload.toDelete || op.payload.linkedElements || []), serverData);
    const prefix = { actionOccurrences: 'occurrence', projectEvents: 'event' }[name];
    if (!prefix) return serverData;
    const local = await services.snapshot.getList(projectId, name);
    const localItems = (name === 'actionOccurrences' ? local?.occurrences : local) || [];
    const pendingKeys = new Set(ops.flatMap(op => op.touches || []));
    const isPending = item => pendingKeys.has(`${prefix}:${item.id}`);
    // Création déjà envoyée dont la réponse s'est perdue : pas de version locale si le serveur a déjà l'élément (même action, élément et jour ;
    // même évènement et jour)
    const sentCreationKeys = new Set(ops.filter(op => isCreationType(op.type) && op.attempts).flatMap(op => op.touches || []));
    const day = date => (date && !isNaN(new Date(date)) ? new Date(date).toISOString().slice(0, 10) : null);
    const isSame = (serverItem, item) => day(serverItem.date) === day(item.date) && (prefix === 'occurrence'
        ? serverItem.actionId === item.actionId && serverItem.elementId === item.elementId
        : serverItem.eventId === item.eventId);
    const merge = items => [
        ...items.filter(item => !isPending(item)),
        ...localItems.filter(item => isPending(item) && !(sentCreationKeys.has(`${prefix}:${item.id}`) && items.some(serverItem => !isPending(serverItem) && isSame(serverItem, item))))
    ];
    if (name !== 'actionOccurrences') return merge(serverData);
    // Liaisons à une série et retraits en file réappliqués, comme leur réponse locale
    let series = serverData.series || [], occurrences = merge(serverData.occurrences || []);
    ops.forEach(({ type, payload }) => {
        if (type === 'series.addElements')
            series = series.map(item => (item.id === payload.seriesId
                ? { ...item, elements: [...(item.elements || []).filter(element => !payload.elements.some(added => added.elementId === element.elementId)), ...payload.elements.map(element => ({ seriesId: payload.seriesId, ...element }))] }
                : item));
        if (type === 'series.removeElements') {
            series = series.map(item => (item.id === payload.seriesId ? { ...item, elements: (item.elements || []).filter(element => !payload.elementIds.includes(element.elementId)) } : item));
            occurrences = occurrences.filter(occurrence => !(occurrence.seriesId === payload.seriesId && !occurrence.status && payload.elementIds.includes(occurrence.elementId)));
        }
    });
    return { ...serverData, series, occurrences };
};

// Arbres, espaces verts et mobilier créés sur l'appareil et pas encore acceptés, tous projets confondus (quota commun de l'organisation)
export const countPendingCreations = async () => {
    const ops = (isOfflineEnabled() ? await getOfflineServices()?.outbox.list() : null) || [];
    return ops
        .filter(op => ['Arbre', 'Espace vert', 'Mobilier'].includes(op.target?.category))
        .reduce((count, op) => count + (op.type === 'element.create' ? 1 : op.type === 'element.bulkCreate' ? (op.payload?.features?.length || 0) : 0), 0);
};

// Écritures de la copie en cours, par catégorie : un chargement n'est complété que si elles ont toutes réussi
const pendingWrites = new Map();
// Suit une écriture de la copie
export const trackSnapshotWrite = (category, promise) => {
    if (!promise) return promise;
    pendingWrites.set(category, [...(pendingWrites.get(category) || []), promise]);
    promise.catch(console.error);
    return promise;
};
// Attend les écritures suivies de la catégorie ; true si toutes ont réussi
export const waitForSnapshotWrites = async (category) => {
    const writes = pendingWrites.get(category) || [];
    pendingWrites.delete(category);
    const results = await Promise.allSettled(writes);
    return results.every(result => result.status === 'fulfilled');
};

// Enregistre les éléments reçus de l'API (versions serveur, saisies locales gardées)
export const saveElementsToSnapshot = (projectId, category, features) => {
    trackSnapshotWrite(category, getSnapshot()?.saveServerElements(projectId, category, features));
};

// Note des suppressions faites côté serveur
export const removeElementsFromSnapshot = (projectId, category, ids) => {
    getSnapshot()?.removeElements(projectId, category, ids).catch(console.error);
};

// Remplace une catégorie lue d'un seul appel (repères, stations)
export const replaceElementsInSnapshot = (projectId, category, features) => {
    getSnapshot()?.replaceServerElements(projectId, category, features).catch(console.error);
};

// Éléments d'une catégorie tels qu'affichés (version locale si modifiée) ; undefined hors pilote
export const readElementsFromSnapshot = async (projectId, category) => {
    const records = await getSnapshot()?.getElementsByCategory(projectId, category);
    return records ? records.map(getDisplayFeature).filter(Boolean) : undefined;
};

// Projet ouvert depuis sa copie : ses lectures sont servies par la copie sans attendre l'API (undefined pour les autres)
let snapshotFirstProjectId = null;
// Déclare le projet ouvert depuis sa copie (null : aucun)
export const setSnapshotFirstProject = (projectId) => {
    snapshotFirstProjectId = projectId === null || projectId === undefined ? null : Number(projectId);
};
const isSnapshotFirst = (projectId) => snapshotFirstProjectId !== null && Number(projectId) === snapshotFirstProjectId;
export const readListSnapshotFirst = async (projectId, name) => (isSnapshotFirst(projectId) ? readListFromSnapshot(projectId, name) : undefined);
export const readElementsSnapshotFirst = async (projectId, category) => (isSnapshotFirst(projectId) ? readElementsFromSnapshot(projectId, category) : undefined);

// Projet ouvert rafraîchi par ProjectMap après une coupure (pilote) : le toast « Recharger » d'App.js ne s'affiche pas
let isProjectRefreshEnabled = false;
export const setProjectRefreshActive = (value) => {
    isProjectRefreshEnabled = !!value;
};
export const isProjectRefreshActive = () => isProjectRefreshEnabled;
